import { useEffect, useRef, useState } from 'react';
import Fab from '@material-ui/core/Fab';
import Paper from '@material-ui/core/Paper';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Avatar from '@material-ui/core/Avatar';
import SvgIcon, { type SvgIconProps } from '@material-ui/core/SvgIcon';
import CloseIcon from '@material-ui/icons/Close';
import SendIcon from '@material-ui/icons/Send';
import AddCommentIcon from '@material-ui/icons/AddComment';
import { makeStyles } from '@material-ui/core/styles';
import { useAiAssistant } from '../../context/AiAssistantContext';
import { Coachmark } from '../../coachmarks/Coachmark';
import {
  clampFabPos,
  computePanelStyle,
  defaultFabPos,
  DRAG_THRESHOLD,
  FAB_POS_KEY,
  FAB_SIZE,
  MARGIN,
  type Pos,
} from './dragPosition';

const INTRO_SEEN_KEY = 'aiWidget.introSeen';

/** The "auto awesome" sparkle (a big 4-point star + two small ones) — @material-ui/icons v4 lacks it. */
const SparkleIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <path d="M19 9l1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25L19 9zm-7.5.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12l-5.5-2.5zM19 15l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25L19 15z" />
  </SvgIcon>
);

// Widget accent — a violet, deliberately distinct from the app's teal primary.
const ACCENT = '#6C4BF6';
const ACCENT_DARK = '#4A2FD6';

const useStyles = makeStyles(theme => ({
  fab: {
    // top/left are set inline from drag state; the Fab is draggable and remembers where you drop it.
    position: 'fixed',
    zIndex: 1200,
    backgroundColor: ACCENT,
    color: '#fff',
    cursor: 'grab',
    touchAction: 'none',
    '&:active': { cursor: 'grabbing' },
    '&:hover': { backgroundColor: ACCENT_DARK },
  },
  intro: {
    // One-time first-run nudge, anchored above the Fab (position set inline from fabPos).
    position: 'fixed',
    width: 244,
    padding: theme.spacing(1.5),
    borderRadius: 12,
    zIndex: 1200,
    borderLeft: `4px solid ${ACCENT}`,
    backgroundColor: theme.palette.background.paper,
  },
  panel: {
    // top/left/transformOrigin are set inline, anchored to the Fab's current position.
    position: 'fixed',
    width: 400,
    maxWidth: 'calc(100vw - 32px)',
    height: 580,
    // vh fallback; on mobile use dvh so the composer isn't hidden under the browser URL bar.
    maxHeight: 'calc(100vh - 48px)',
    '@supports (height: 100dvh)': { maxHeight: 'calc(100dvh - 48px)' },
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    borderRadius: 16,
    zIndex: 1200,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    color: '#fff',
    background: `linear-gradient(135deg, ${ACCENT_DARK} 0%, ${ACCENT} 100%)`,
  },
  headerAvatar: {
    backgroundColor: 'rgba(255,255,255,0.25)',
    color: '#fff',
    width: 34,
    height: 34,
  },
  headerText: { flex: 1, lineHeight: 1.15 },
  headerActions: { color: '#fff' },
  transcript: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    backgroundColor: theme.palette.background.default,
  },
  row: { display: 'flex', gap: theme.spacing(1), alignItems: 'flex-end' },
  rowUser: { flexDirection: 'row-reverse' },
  msgCol: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(0.75),
    // Wider column + (for user rows) hug the right edge, since the user has no avatar — otherwise the
    // bubble left-aligns inside its column and leaves dead space on the right.
    maxWidth: '92%',
  },
  msgColUser: { alignItems: 'flex-end' },
  meta: {
    color: theme.palette.text.secondary,
    fontSize: '0.68rem',
    lineHeight: 1.2,
    padding: theme.spacing(0, 0.5),
    userSelect: 'text',
  },
  optionChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  capabilities: {
    textAlign: 'left',
    margin: 0,
    paddingLeft: theme.spacing(2.5),
    color: theme.palette.text.secondary,
  },
  botAvatar: {
    width: 26,
    height: 26,
    backgroundColor: ACCENT,
    color: '#fff',
  },
  bubble: {
    padding: theme.spacing(1, 1.5),
    borderRadius: 14,
    // Fill the message column (itself capped at 92%): an 80% cap here made a short bubble hug-right in
    // a narrow box, wrapping text that had plenty of room. The column cap already keeps the gutter.
    maxWidth: '100%',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    boxShadow: theme.shadows[1],
  },
  user: {
    borderBottomRightRadius: 4,
    backgroundColor: ACCENT,
    color: '#fff',
  },
  assistant: {
    borderBottomLeftRadius: 4,
    backgroundColor: theme.palette.background.paper,
  },
  clarify: {
    borderBottomLeftRadius: 4,
    backgroundColor: theme.palette.info.main,
    color: theme.palette.info.contrastText,
  },
  error: {
    borderBottomLeftRadius: 4,
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
  empty: {
    margin: 'auto',
    textAlign: 'center',
    color: theme.palette.text.secondary,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
  },
  examples: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    justifyContent: 'center',
  },
  typing: { display: 'flex', gap: 4, padding: theme.spacing(0.5, 0.5) },
  dot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    backgroundColor: theme.palette.text.secondary,
    animation: '$blink 1.2s infinite ease-in-out both',
    // Honor the OS "reduce motion" setting — no perpetual pulse for vestibular-sensitive users.
    '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 0.6 },
  },
  dot2: { animationDelay: '0.2s' },
  dot3: { animationDelay: '0.4s' },
  '@keyframes blink': {
    '0%, 80%, 100%': { opacity: 0.2 },
    '40%': { opacity: 1 },
  },
  recap: {
    padding: theme.spacing(1.25, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.action.selected,
  },
  recapHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  recapActions: { display: 'flex', gap: theme.spacing(1), marginTop: theme.spacing(1) },
  inputRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  sendBtn: { minWidth: 44, borderRadius: 12 },
}));

/** Short local time (e.g. "2:31 PM") for a message's timestamp; blank on a bad value. */
function formatTime(ts: number): string {
  try {
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

// One example per capability, each self-contained so a first click actually does something (create
// drives the scaffolder; the question + edit resolve a real lambda) rather than dead-ending in a clarify.
const EXAMPLES = [
  'Create a new Phoenix API',
  'What is the memory of awb_accreditation_body_version_get?',
  'Set awb_accreditation_body_version_get timeout to 30 seconds',
  'Add a lambda to an API',
];

/**
 * The bottom-right copilot: a Fab that opens a chat panel. The panel drives the config-editor via the
 * shared context + driver; the global <Coachmark> overlay narrates the live UI drive. Mount only when
 * aiAssistant.enabled is true (App.tsx gates it).
 */
export const AiAssistantWidget = ({
  showLauncher = true,
}: {
  /** Hide the launcher Fab/panel (e.g. on the scaffolder wizard) while keeping the Coachmark overlay. */
  showLauncher?: boolean;
}) => {
  const classes = useStyles();
  const {
    messages,
    sending,
    sendMessage,
    clearChat,
    driverStatus,
    currentStep,
    activeStepIndex,
    totalSteps,
    next,
  } = useAiAssistant();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const transcriptRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const prevStatusRef = useRef(driverStatus);

  // Draggable Fab: `fabPos` is its top-left; it starts top-right, remembers where you drop it
  // (localStorage), and the chat panel anchors to it. `movedRef` distinguishes a drag from a click.
  const [fabPos, setFabPos] = useState<Pos | null>(null);
  const fabPosRef = useRef<Pos | null>(null);
  const movedRef = useRef(false);

  // Keyboard focus: the Fab is unmounted while the panel is open, so on close, restore focus to it
  // (otherwise a keyboard user is dumped at the top of the page). The input autofocuses on open.
  const fabRef = useRef<HTMLButtonElement>(null);
  const prevOpenRef = useRef(open);
  useEffect(() => {
    // Focus the composer on open, and restore focus to the Fab on close. Done imperatively (not via
    // the autoFocus prop, which jsx-a11y/no-autofocus forbids).
    if (!prevOpenRef.current && open) inputRef.current?.focus();
    if (prevOpenRef.current && !open) fabRef.current?.focus();
    prevOpenRef.current = open;
  }, [open]);

  // First-run nudge: shown once (localStorage-gated) so a new user learns the copilot exists without
  // having to click an unlabeled icon. Default true to avoid a flash before storage is read.
  const [introSeen, setIntroSeen] = useState(true);
  useEffect(() => {
    try {
      setIntroSeen(window.localStorage.getItem(INTRO_SEEN_KEY) === '1');
    } catch {
      /* ignore */
    }
  }, []);
  const dismissIntro = () => {
    setIntroSeen(true);
    try {
      window.localStorage.setItem(INTRO_SEEN_KEY, '1');
    } catch {
      /* ignore */
    }
  };

  const setPos = (p: Pos) => {
    fabPosRef.current = p;
    setFabPos(p);
  };

  useEffect(() => {
    let saved: Pos | null = null;
    try {
      const raw = window.localStorage.getItem(FAB_POS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        // Validate the stored shape — a corrupt/legacy value would otherwise clamp to NaN and put the
        // Fab off-screen.
        if (
          parsed &&
          typeof parsed.top === 'number' &&
          Number.isFinite(parsed.top) &&
          typeof parsed.left === 'number' &&
          Number.isFinite(parsed.left)
        ) {
          saved = { top: parsed.top, left: parsed.left };
        }
      }
    } catch {
      // ignore malformed/absent storage
    }
    const { innerWidth: vw, innerHeight: vh } = window;
    setPos(clampFabPos(saved ?? defaultFabPos(vw, vh), vw, vh));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the Fab on-screen when the window is resized (a saved bottom-right spot goes off-screen if the
  // viewport shrinks).
  useEffect(() => {
    const onResize = () => {
      if (fabPosRef.current) {
        setPos(clampFabPos(fabPosRef.current, window.innerWidth, window.innerHeight));
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close the panel when the launcher is hidden (navigating onto the scaffolder wizard), so it doesn't
  // linger as open=true and reappear when returning to another route.
  useEffect(() => {
    if (!showLauncher) setOpen(false);
  }, [showLauncher]);

  const onFabPointerDown = (e: React.PointerEvent) => {
    const start = fabPosRef.current;
    if (!start) return;
    movedRef.current = false;
    const startX = e.clientX;
    const startY = e.clientY;
    const offX = startX - start.left;
    const offY = startY - start.top;

    const onMove = (ev: PointerEvent) => {
      // Ignore sub-threshold jitter: don't move the Fab (or flag a drag) until the pointer has
      // travelled past the threshold, so a shaky click stays a click and doesn't nudge the Fab.
      if (
        !movedRef.current &&
        Math.abs(ev.clientX - startX) <= DRAG_THRESHOLD &&
        Math.abs(ev.clientY - startY) <= DRAG_THRESHOLD
      ) {
        return;
      }
      movedRef.current = true;
      setPos(
        clampFabPos(
          { top: ev.clientY - offY, left: ev.clientX - offX },
          window.innerWidth,
          window.innerHeight,
        ),
      );
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      if (movedRef.current && fabPosRef.current) {
        try {
          window.localStorage.setItem(FAB_POS_KEY, JSON.stringify(fabPosRef.current));
        } catch {
          // ignore storage failures
        }
      }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const panelStyle = fabPos
    ? computePanelStyle(fabPos, window.innerWidth, window.innerHeight)
    : undefined;

  // Keep the newest message / typing indicator in view — on new messages, while typing, AND when the
  // panel (re)opens with a restored transcript. The ref only exists once the panel is mounted, so the
  // initial restore needs `open` in the deps to scroll to the bottom instead of showing the top.
  useEffect(() => {
    if (!open) return;
    const el = transcriptRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending, open]);

  // Step aside: when a drive begins (idle -> any active state), collapse the chat panel to the Fab so
  // the coach-mark isn't stacked on top of the chat (they both live bottom-right). Reopen any time.
  useEffect(() => {
    if (prevStatusRef.current === 'idle' && driverStatus !== 'idle') {
      setOpen(false);
    }
    prevStatusRef.current = driverStatus;
  }, [driverStatus]);

  const submit = async (text: string) => {
    const value = text.trim();
    if (!value || sending) return;
    setInput('');
    const ok = await sendMessage(value);
    // On a failure (couldn't reach the assistant / under-specified), put the text back so the user can
    // tweak and resend instead of retyping from scratch.
    if (!ok) setInput(value);
  };

  const showRecap =
    !!currentStep &&
    (driverStatus === 'running' || driverStatus === 'awaiting-user' || driverStatus === 'done');

  const bubbleClass = (m: { role: string; kind: string }) => {
    if (m.role === 'user') return classes.user;
    if (m.kind === 'error') return classes.error;
    if (m.kind === 'clarify') return classes.clarify;
    return classes.assistant;
  };

  return (
    <>
      {showLauncher && !open && fabPos && (
        <Fab
          ref={fabRef}
          size="small"
          className={classes.fab}
          style={{ top: fabPos.top, left: fabPos.left }}
          onPointerDown={onFabPointerDown}
          onClick={() => {
            // A drag ends with a click too — only open when the pointer didn't move. Reset the flag
            // afterwards so a later keyboard activation (no pointer events) still opens.
            const moved = movedRef.current;
            movedRef.current = false;
            if (!moved) setOpen(true);
          }}
          aria-label="Ask AI — the config copilot for creating and editing APIs"
          title="Ask AI — create or edit an API · drag to move"
          data-testid="ai-widget-fab"
        >
          <SparkleIcon fontSize="small" />
        </Fab>
      )}

      {showLauncher && !open && fabPos && !introSeen && (
        <Paper
          elevation={10}
          className={classes.intro}
          data-testid="ai-widget-intro"
          role="dialog"
          aria-label="About the config copilot"
          style={{
            top: Math.max(MARGIN, fabPos.top - 138),
            left: Math.max(MARGIN, fabPos.left + FAB_SIZE - 244),
          }}
        >
          <Typography
            variant="body2"
            style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <SparkleIcon fontSize="small" /> Meet the Config Copilot
          </Typography>
          <Typography variant="caption" component="div" style={{ marginTop: 4 }}>
            Describe a change in plain English — “create a Phoenix API” or “set a lambda’s timeout”
            — and I’ll walk you through it in the real editor. You review &amp; apply.
          </Typography>
          <Box style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={dismissIntro} data-testid="ai-widget-intro-dismiss">
              Dismiss
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => {
                dismissIntro();
                setOpen(true);
              }}
            >
              Try it
            </Button>
          </Box>
        </Paper>
      )}

      {showLauncher && open && (
        <Paper
          className={classes.panel}
          style={panelStyle}
          elevation={12}
          data-testid="ai-widget-panel"
          role="dialog"
          aria-labelledby="ai-copilot-title"
        >
          <Box className={classes.header}>
            <Avatar className={classes.headerAvatar}>
              <SparkleIcon fontSize="small" />
            </Avatar>
            <Box className={classes.headerText}>
              <Typography
                id="ai-copilot-title"
                component="h2"
                variant="subtitle1"
                style={{ fontWeight: 600 }}
              >
                Config Copilot
              </Typography>
              <Typography variant="caption" style={{ opacity: 0.85 }}>
                Guides your changes — you review &amp; apply
              </Typography>
            </Box>
            <IconButton
              size="small"
              className={classes.headerActions}
              onClick={clearChat}
              aria-label="Start a new conversation"
              data-testid="ai-widget-clear"
              title="Start a new conversation"
            >
              <AddCommentIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              className={classes.headerActions}
              onClick={() => setOpen(false)}
              aria-label="Close config copilot"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <div
            className={classes.transcript}
            data-testid="ai-widget-transcript"
            ref={transcriptRef}
            role="log"
            aria-live="polite"
            aria-relevant="additions text"
            aria-label="Conversation with the config copilot"
          >
            {messages.length === 0 && !sending && (
              <Box className={classes.empty}>
                <Avatar className={classes.botAvatar} style={{ width: 44, height: 44 }}>
                  <SparkleIcon />
                </Avatar>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  I guide you through API &amp; Step-Function config changes.
                </Typography>
                <Typography variant="caption" component="div">
                  Describe a change in plain English and I&apos;ll walk you through it in the real
                  editor, step by step. <strong>I never apply changes myself</strong> — you review
                  every step and click Save / Create. Treat these like any infra change: review
                  carefully before you submit.
                  <ul className={classes.capabilities}>
                    <li>Change a lambda&apos;s memory, timeout, snapstart or env vars</li>
                    <li>Add a new lambda to an API, or create a new API / Step Function</li>
                    <li>Read a Jira ticket and turn it into the change</li>
                  </ul>
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Try:
                </Typography>
                <Box className={classes.examples}>
                  {EXAMPLES.map(ex => (
                    <Chip
                      key={ex}
                      size="small"
                      variant="outlined"
                      label={ex}
                      onClick={() => void submit(ex)}
                      clickable
                    />
                  ))}
                </Box>
              </Box>
            )}

            {messages.map(m => (
              <Box
                key={m.id}
                className={`${classes.row} ${m.role === 'user' ? classes.rowUser : ''}`}
              >
                {m.role === 'assistant' && (
                  <Avatar className={classes.botAvatar}>
                    <SparkleIcon style={{ fontSize: 16 }} />
                  </Avatar>
                )}
                <Box className={`${classes.msgCol} ${m.role === 'user' ? classes.msgColUser : ''}`}>
                  {/* Actor + time — selectable so it is included when the user copies a message. */}
                  <Typography variant="caption" className={classes.meta}>
                    {m.role === 'user' ? 'You' : 'Assistant'}
                    {m.ts ? ` · ${formatTime(m.ts)}` : ''}
                  </Typography>
                  <Box className={`${classes.bubble} ${bubbleClass(m)}`}>
                    <Typography variant="body2">{m.text}</Typography>
                  </Box>
                  {m.options && m.options.length > 0 && (
                    <Box className={classes.optionChips} data-testid="ai-widget-options">
                      {m.options.map(opt => (
                        <Chip
                          key={opt}
                          size="small"
                          variant="outlined"
                          label={opt}
                          onClick={() => void submit(opt)}
                          clickable
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            ))}

            {sending && (
              <Box className={classes.row}>
                <Avatar className={classes.botAvatar}>
                  <SparkleIcon style={{ fontSize: 16 }} />
                </Avatar>
                <Box
                  className={`${classes.bubble} ${classes.assistant}`}
                  data-testid="ai-widget-typing"
                >
                  <Box className={classes.typing}>
                    <span className={classes.dot} />
                    <span className={`${classes.dot} ${classes.dot2}`} />
                    <span className={`${classes.dot} ${classes.dot3}`} />
                  </Box>
                </Box>
              </Box>
            )}
          </div>

          {showRecap && currentStep && (
            <Box className={classes.recap} data-testid="ai-widget-recap">
              <Box className={classes.recapHead}>
                <Typography variant="caption" color="textSecondary">
                  {totalSteps > 0 ? `Step ${activeStepIndex + 1} of ${totalSteps}` : ''}
                </Typography>
                {driverStatus === 'done' && (
                  <Typography variant="caption" color="primary" style={{ fontWeight: 600 }}>
                    Ready — review & submit
                  </Typography>
                )}
              </Box>
              <Typography variant="body2">{currentStep.tooltip}</Typography>
              {driverStatus === 'awaiting-user' && (
                <Box className={classes.recapActions}>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={next}
                    data-testid="ai-widget-next"
                  >
                    Next step
                  </Button>
                </Box>
              )}
            </Box>
          )}

          <Box className={classes.inputRow}>
            <TextField
              fullWidth
              multiline
              inputRef={inputRef}
              maxRows={3}
              size="small"
              variant="outlined"
              placeholder="e.g. “create a phoenix API” or “set awb_get timeout to 30s”"
              value={input}
              disabled={sending}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void submit(input);
                } else if (e.key === 'Escape') {
                  setOpen(false);
                }
              }}
              inputProps={{
                'data-testid': 'ai-widget-input',
                'aria-label': 'Ask the config copilot',
              }}
            />
            <Button
              variant="contained"
              color="primary"
              className={classes.sendBtn}
              onClick={() => void submit(input)}
              disabled={sending || input.trim().length === 0}
              data-testid="ai-widget-send"
              aria-label="Send"
            >
              <SendIcon fontSize="small" />
            </Button>
          </Box>
        </Paper>
      )}

      <Coachmark />
    </>
  );
};
